"use strict";

/* ABMIUM lean storefront shell for custom product, catalogue and editorial routes. */
function debounce(func, wait, immediate) {
  let timeout;
  return function () {
    const context = this;
    const args = arguments;
    const later = () => {
      timeout = null;
      if (!immediate) func.apply(context, args);
    };
    const callNow = immediate && !timeout;
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
    if (callNow) func.apply(context, args);
  };
}

function fetchConfig(type = "json") {
  return {
    method: "POST",
    headers: { "Content-Type": "application/json", Accept: `application/${type}` },
  };
}

function getFocusableElements(container) {
  return Array.from(container.querySelectorAll("summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"));
}

const trapFocusHandlers = {};
function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener("focusin", trapFocusHandlers.focusin);
  document.removeEventListener("focusout", trapFocusHandlers.focusout);
  document.removeEventListener("keydown", trapFocusHandlers.keydown);
  elementToFocus?.focus?.();
}

function trapFocus(container, elementToFocus = container) {
  const elements = getFocusableElements(container);
  const first = elements[0];
  const last = elements[elements.length - 1];
  removeTrapFocus();
  if (!first || !last) return;
  trapFocusHandlers.focusin = (event) => {
    if (event.target !== container && event.target !== last && event.target !== first) return;
    document.addEventListener("keydown", trapFocusHandlers.keydown);
  };
  trapFocusHandlers.focusout = () => document.removeEventListener("keydown", trapFocusHandlers.keydown);
  trapFocusHandlers.keydown = (event) => {
    if (event.code.toUpperCase() !== "TAB") return;
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    } else if ((event.target === container || event.target === first) && event.shiftKey) {
      event.preventDefault();
      last.focus();
    }
  };
  document.addEventListener("focusout", trapFocusHandlers.focusout);
  document.addEventListener("focusin", trapFocusHandlers.focusin);
  elementToFocus?.focus?.();
}

function pauseAllMedia() {
  document.querySelectorAll(".js-youtube").forEach((video) => video.contentWindow?.postMessage('{"event":"command","func":"pauseVideo","args":""}', "*"));
  document.querySelectorAll(".js-vimeo").forEach((video) => video.contentWindow?.postMessage('{"method":"pause"}', "*"));
  document.querySelectorAll("video").forEach((video) => video.pause());
}
window.pauseAllMedia = pauseAllMedia;

const touchDevice = "ontouchstart" in window || navigator.maxTouchPoints > 0 || navigator.msMaxTouchPoints > 0;
if (touchDevice) {
  const subMenuNavs = document.querySelectorAll(".has-sub-menu > a");
  subMenuNavs.forEach((mainMenuNav) => {
    mainMenuNav.addEventListener("click", (event) => {
      subMenuNavs.forEach((item) => {
        if (item !== mainMenuNav) item.classList.remove("is-dropdown-open");
      });
      if (mainMenuNav.classList.contains("is-dropdown-open")) return;
      event.preventDefault();
      mainMenuNav.classList.add("is-dropdown-open");
    });
  });
}

const mobileMenuWrap = document.querySelector(".head-slot-nav-mobile-link-wrapper");
const mobileNavWrap = document.querySelector(".navigation-mobile-wrap");
const mobileMenuToggle = document.querySelector(".head-slot-nav-mobile-link");
const mobileMenu = document.querySelector(".mobile-menu");
const expandOnButton = mobileMenu?.dataset.buttonExpand != null;

function setMobileMenuMaxheight() {
  if (!mobileMenu) return;
  const header = document.querySelector(".header, #header");
  const bottom = header?.getBoundingClientRect().bottom || 0;
  mobileMenu.style.maxHeight = `${Math.max(240, window.innerHeight - bottom + 70)}px`;
}

function handleMobileMenuOpen() {
  if (!mobileMenu || !mobileMenuToggle) return;
  document.querySelector("sticky-header")?.reveal?.();
  document.body.classList.add("mobile-menu-open");
  mobileMenuToggle.ariaExpanded = "true";
  mobileMenu.ariaHidden = "false";
  if (mobileMenuWrap && mobileNavWrap) trapFocus(mobileMenuWrap, mobileNavWrap);
  setMobileMenuMaxheight();
  document.dispatchEvent(new CustomEvent("mobile-menu:open"));
}

function handleMobileMenuDismiss() {
  if (!mobileMenu || !mobileMenuToggle) return;
  document.body.classList.remove("mobile-menu-open");
  mobileMenuToggle.ariaExpanded = "false";
  mobileMenu.ariaHidden = "true";
  removeTrapFocus(mobileMenuToggle);
  mobileMenu.style.maxHeight = "";
  document.dispatchEvent(new CustomEvent("mobile-menu:close"));
}

document.body.addEventListener("click", (event) => {
  const target = event.target;
  const mobileNav = target.closest?.(".navigation-mobile-item");
  const mobileButton = target.closest?.(".head-slot-nav-mobile-link");
  const anchor = target.closest?.('a[href^="#"]');

  if (mobileNav && mobileNav.querySelector("ul")) {
    if (expandOnButton) {
      if (target.closest?.(".navigation-mobile-item-link-expand")) {
        event.preventDefault();
        mobileNav.classList.toggle("menu-item-expanded");
      }
    } else if (target.closest?.(".navigation-mobile-item-link")) {
      event.preventDefault();
      mobileNav.classList.toggle("menu-item-expanded");
    }
    const expanded = mobileNav.classList.contains("menu-item-expanded");
    const button = mobileNav.querySelector(".navigation-mobile-item-link-expand");
    button?.setAttribute("aria-expanded", expanded ? "true" : "false");
  }

  if (mobileButton) {
    event.preventDefault();
    document.body.classList.contains("mobile-menu-open") ? handleMobileMenuDismiss() : handleMobileMenuOpen();
    return;
  }
  if (anchor && document.body.classList.contains("mobile-menu-open")) handleMobileMenuDismiss();
  if (document.body.classList.contains("mobile-menu-open") && !target.closest?.(".mobile-menu") && !mobileButton) handleMobileMenuDismiss();
});

if (!customElements.get("deferred-media")) {
  customElements.define("deferred-media", class DeferredMedia extends HTMLElement {
    constructor() {
      super();
      this.querySelector('[id^="Deferred-Poster-"]')?.addEventListener("click", () => this.loadContent());
    }
    loadContent(focus = true) {
      window.pauseAllMedia();
      if (this.hasAttribute("loaded")) return;
      const template = this.querySelector("template");
      if (!template?.content?.firstElementChild) return;
      const content = template.content.firstElementChild.cloneNode(true);
      this.setAttribute("loaded", true);
      const media = content.matches?.("video, model-viewer, iframe") ? content : content.querySelector?.("video, model-viewer, iframe");
      if (!media) return;
      const deferredElement = this.appendChild(media);
      if (focus) deferredElement.focus?.();
    }
  });
}

/* Customer-specific discounts are calculated by Shopify in-cart. Surface the verified result on the PDP without promising a price to ineligible visitors. */
(() => {
  const root = document.querySelector('[data-abm-product]');
  if (!root) return;
  const header = root.querySelector('.abm-buy__header');
  const variantInput = root.querySelector('[data-abm-variant-id]');
  if (!header || !variantInput) return;

  const note = document.createElement('p');
  note.className = 'abm-buy__tax abm-buy__account-pricing';
  note.textContent = 'Eligible institutional pricing is applied automatically in cart.';
  header.appendChild(note);
  let lastCart = null;

  const formatMoney = (cents, currency) => {
    const locale = document.documentElement.lang || 'en-GB';
    try {
      return new Intl.NumberFormat(locale, { style: 'currency', currency: currency || 'GBP', currencyDisplay: 'narrowSymbol' }).format((Number(cents) || 0) / 100);
    } catch (_) {
      return `${currency || 'GBP'} ${((Number(cents) || 0) / 100).toFixed(2)}`;
    }
  };

  const renderCartPricing = (cart) => {
    if (!cart?.items) return;
    lastCart = cart;
    const variantId = Number(variantInput.value);
    const line = cart.items.find((item) => Number(item.variant_id || item.id) === variantId);
    const original = Number(line?.original_price) || 0;
    const finalPrice = Number(line?.final_price ?? line?.discounted_price) || 0;
    if (!line || !original || finalPrice >= original) {
      note.textContent = 'Eligible institutional pricing is applied automatically in cart.';
      return;
    }
    const title = line.discounts?.[0]?.title || 'Institutional pricing';
    const percent = Math.round(((original - finalPrice) / original) * 100);
    note.textContent = `${title}: ${percent}% applied in cart · ${formatMoney(finalPrice, cart.currency)} each`;
  };

  document.addEventListener('abmium:cart-synced', (event) => renderCartPricing(event.detail?.cart));
  root.addEventListener('click', () => window.setTimeout(() => { if (lastCart) renderCartPricing(lastCart); }, 0));
  root.addEventListener('change', () => window.setTimeout(() => { if (lastCart) renderCartPricing(lastCart); }, 0));
})();
